import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { Dimensions } from 'react-native';
import {
    Button,
    ButtonSize,
    ButtonVariant,
    IconButton,
    Sheet,
    SmoothieScrollView,
    SpinnerIcon,
    Trash2Icon,
    View,
    XIcon,
    XStack,
    YStack,
} from 'smoothie';

import { APP_HEADER_HEIGHT } from '../../../common/components/AppHeader';
import { APP_RIGHT_EDGE } from '../../../common/utils/layout';

interface FormDrawerProps {
    children: ReactNode;
    onClose?: () => void;
    /**
     * - `drawer`: Fixed-position right-side panel (480px wide).
     * - `modal`: Bottom sheet modal with its own close button.
     * - `inline`: Fills its parent container (no positioning), with an X close
     *   button at the top-right and a sticky save/delete footer. Use this when
     *   the parent (e.g. an animated slide-in panel) controls position/size.
     */
    presentation?: 'drawer' | 'modal' | 'inline';
    onSave?: () => void;
    saveDisabled?: boolean;
    /** Renders a danger Delete control on the opposite side of Save (e.g. existing goal / rule). */
    onDelete?: () => void;
    deleteDisabled?: boolean;
    /** When true, the Save button shows "Saving" with a rotating spinner icon and both buttons are disabled. */
    isSaving?: boolean;
    /** When true, the Delete button shows "Deleting" with a rotating spinner icon and both buttons are disabled. */
    isDeleting?: boolean;
}

const FormDrawer = ({
    children,
    onClose,
    presentation = 'drawer',
    onSave,
    saveDisabled = false,
    onDelete,
    deleteDisabled = false,
    isSaving = false,
    isDeleting = false,
}: FormDrawerProps) => {
    const inFlight = isSaving || isDeleting;
    const deleteButtonText = isDeleting ? 'Deleting' : 'Delete';
    const saveButtonText = isSaving ? 'Saving' : 'Save';
    const deleteButtonIcon = isDeleting ? <SpinnerIcon /> : Trash2Icon;
    const saveButtonIcon = isSaving ? <SpinnerIcon /> : undefined;
    const [screenHeight, setScreenHeight] = useState(
        Dimensions.get('window').height,
    );

    // Update the screenHeight on window resize events.
    useEffect(() => {
        const onChange = ({ window }) => {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument,@typescript-eslint/no-unsafe-member-access
            setScreenHeight(window.height);
        };

        const subscription = Dimensions.addEventListener('change', onChange);

        return () => {
            subscription.remove();
        };
    }, []);

    const dynamicHeight = screenHeight - APP_HEADER_HEIGHT;

    if (presentation === 'inline') {
        return (
            <YStack flex={1} height="100%" backgroundColor="$white">
                {onClose != null && (
                    <XStack
                        alignItems="center"
                        justifyContent="flex-end"
                        paddingHorizontal={16}
                        paddingTop={16}
                    >
                        <IconButton
                            variant="secondary"
                            size="tiny"
                            icon={XIcon}
                            onPress={onClose}
                        />
                    </XStack>
                )}
                <SmoothieScrollView
                    flex={1}
                    containerStyle={{
                        width: '100%',
                        padding: 16,
                        '$platform-web': {
                            marginTop: 0,
                        },
                    }}
                >
                    {children}
                </SmoothieScrollView>
                {(onSave != null || onDelete != null) && (
                    <XStack
                        paddingHorizontal={16}
                        paddingTop={16}
                        paddingBottom={24}
                        width="100%"
                        alignItems="center"
                        justifyContent={
                            onDelete != null ? 'space-between' : 'flex-end'
                        }
                        borderTopWidth={1}
                        borderTopColor="$gray400"
                        backgroundColor="$white"
                    >
                        {onDelete != null && (
                            <Button
                                disabled={inFlight || deleteDisabled}
                                variant={ButtonVariant.TERTIARY}
                                size={ButtonSize.SMALL}
                                text={deleteButtonText}
                                tone="danger"
                                icon={deleteButtonIcon}
                                onPress={onDelete}
                                buttonProps={{
                                    width: 'auto',
                                }}
                            />
                        )}
                        {onSave != null && (
                            <Button
                                disabled={inFlight || saveDisabled}
                                variant={ButtonVariant.PRIMARY}
                                size={ButtonSize.SMALL}
                                text={saveButtonText}
                                icon={saveButtonIcon}
                                onPress={onSave}
                                buttonProps={{
                                    width: 'auto',
                                }}
                            />
                        )}
                    </XStack>
                )}
            </YStack>
        );
    }

    if (presentation === 'modal') {
        return (
            <Sheet
                isOpen
                showCloseButton
                onClose={() => {
                    onClose?.();
                }}
            >
                <YStack flex={1} paddingTop={24} gap={24}>
                    <YStack>{children}</YStack>
                    {(onSave != null || onDelete != null) && (
                        <XStack
                            position="sticky"
                            bottom={0}
                            paddingVertical={16}
                            paddingBottom={24}
                            paddingHorizontal={4}
                            width="100%"
                            alignItems="center"
                            justifyContent={
                                onDelete != null ? 'space-between' : 'flex-end'
                            }
                            backgroundColor="$white"
                        >
                            {onDelete != null && (
                                <Button
                                    disabled={inFlight || deleteDisabled}
                                    variant={ButtonVariant.TERTIARY}
                                    size={ButtonSize.SMALL}
                                    width="auto"
                                    tone="danger"
                                    icon={deleteButtonIcon}
                                    text={deleteButtonText}
                                    onPress={onDelete}
                                />
                            )}
                            {onSave != null && (
                                <Button
                                    disabled={inFlight || saveDisabled}
                                    variant={ButtonVariant.PRIMARY}
                                    size={ButtonSize.SMALL}
                                    text={saveButtonText}
                                    icon={saveButtonIcon}
                                    onPress={onSave}
                                    width="auto"
                                />
                            )}
                        </XStack>
                    )}
                </YStack>
            </Sheet>
        );
    }

    return (
        <View position="fixed" right={APP_RIGHT_EDGE} height={dynamicHeight}>
            <YStack width={480} height={dynamicHeight} backgroundColor="$white">
                <SmoothieScrollView
                    flex={1}
                    containerStyle={{
                        width: '100%',
                        padding: 16,
                        marginTop: 16,
                        '$platform-web': {
                            marginTop: 0,
                        },
                    }}
                >
                    {children}
                </SmoothieScrollView>
                {(onSave != null || onDelete != null) && (
                    <XStack
                        paddingHorizontal={16}
                        paddingTop={16}
                        paddingBottom={24}
                        width="100%"
                        alignItems="center"
                        justifyContent={
                            onDelete != null ? 'space-between' : 'flex-end'
                        }
                        borderTopWidth={1}
                        borderTopColor="$gray400"
                    >
                        {onDelete != null && (
                            <Button
                                disabled={inFlight || deleteDisabled}
                                variant={ButtonVariant.TERTIARY}
                                size={ButtonSize.SMALL}
                                text={deleteButtonText}
                                tone="danger"
                                icon={deleteButtonIcon}
                                onPress={onDelete}
                                buttonProps={{
                                    width: 'auto',
                                }}
                            />
                        )}
                        {onSave != null && (
                            <Button
                                disabled={inFlight || saveDisabled}
                                variant={ButtonVariant.PRIMARY}
                                size={ButtonSize.SMALL}
                                text={saveButtonText}
                                icon={saveButtonIcon}
                                onPress={onSave}
                                buttonProps={{
                                    width: 'auto',
                                }}
                            />
                        )}
                    </XStack>
                )}
            </YStack>
        </View>
    );
};

export default FormDrawer;
